import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import RenderMonitor from '../components/RenderMonitor';

// Route variants:
// - /client/:id/access
// - /region/:id/access
// location.state may include { scope: 'client'|'region', title?: string }

function ManageAccess() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const scope = location?.state?.scope || (location?.pathname?.includes('/client/') ? 'client' : 'region');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]); // { id, email, full_name, source?: 'client'|'region' }
  const [clientName, setClientName] = useState('');
  const [regionName, setRegionName] = useState('');
  const [regionClientId, setRegionClientId] = useState(location?.state?.clientId || '');
  const [reloadKey, setReloadKey] = useState(0);
  // Add managers form
  const [addEmails, setAddEmails] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addResults, setAddResults] = useState([]);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setError('');
        setUsers([]);
        if (scope === 'client') {
          // Fetch client name
          console.log('[ACCESS_PAGE][DB] SELECT client name', { clientId: id });
          const { data: clientRow } = await supabase
            .from('clients')
            .select('id, business_name')
            .eq('id', id)
            .single();
          if (clientRow) setClientName(clientRow.business_name || '');

          // Fetch client-level admin users
          console.log('[ACCESS_PAGE][DB] SELECT admin_user_clients (user_id only)', { clientId: id });
          const { data: mapRows, error: mapErr } = await supabase
            .from('admin_user_clients')
            .select('user_id')
            .eq('client_id', id);
          if (mapErr) { setError('Failed to load access list'); setLoading(false); return; }
          const userIds = Array.from(new Set((mapRows || []).map(r => String(r.user_id))));
          if (userIds.length === 0) { setUsers([]); setLoading(false); return; }
          const { data: userRows, error: usersErr } = await supabase
            .from('users')
            .select('id, email, full_name')
            .in('id', userIds);
          if (usersErr) { setError('Failed to load users'); setLoading(false); return; }
          setUsers((userRows || []).map(u => ({ id: String(u.id), email: u.email, full_name: u.full_name, source: 'client' })));
        } else {
          // Fetch region info and client name
          console.log('[ACCESS_PAGE][DB] SELECT region info', { regionId: id });
          const { data: regionRow } = await supabase
            .from('regions')
            .select('id, name, client_id')
            .eq('id', id)
            .single();
          const resolvedClientId = regionRow?.client_id;
          if (regionRow?.name) setRegionName(regionRow.name);
          if (resolvedClientId != null) setRegionClientId(String(resolvedClientId));
          if (resolvedClientId != null) {
            const { data: clientRow } = await supabase
              .from('clients')
              .select('id, business_name')
              .eq('id', resolvedClientId)
              .single();
            if (clientRow) setClientName(clientRow.business_name || '');
          }

          // Fetch region-level admin users
          console.log('[ACCESS_PAGE][DB] SELECT admin_user_regions (user_id only)', { regionId: id });
          const { data: regionMap, error: regErr } = await supabase
            .from('admin_user_regions')
            .select('user_id')
            .eq('region_id', id);
          if (regErr) { setError('Failed to load access list'); setLoading(false); return; }
          const regionIds = new Set((regionMap || []).map(r => String(r.user_id)));

          // Fetch client-level admins for this region's client
          let clientIdsSet = new Set();
          if (resolvedClientId != null) {
            console.log('[ACCESS_PAGE][DB] SELECT admin_user_clients (user_id only) for region\'s client', { clientId: resolvedClientId });
            const { data: clientMap, error: cliErr } = await supabase
              .from('admin_user_clients')
              .select('user_id')
              .eq('client_id', resolvedClientId);
            if (!cliErr) clientIdsSet = new Set((clientMap || []).map(r => String(r.user_id)));
          }

          // Union of region and client level user ids
          const unionIds = new Set([...Array.from(regionIds), ...Array.from(clientIdsSet)]);
          if (unionIds.size === 0) { setUsers([]); setLoading(false); return; }
          const unionIdList = Array.from(unionIds);
          const { data: userRows, error: usersErr } = await supabase
            .from('users')
            .select('id, email, full_name')
            .in('id', unionIdList);
          if (usersErr) { setError('Failed to load users'); setLoading(false); return; }
          const list = (userRows || []).map(u => {
            const uid = String(u.id);
            return { id: uid, email: u.email, full_name: u.full_name, source: regionIds.has(uid) ? 'region' : 'client' };
          });
          setUsers(list);
        }
      } catch (e) {
        setError('Unexpected error');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id, scope, reloadKey]);

  const heading = scope === 'client' ? 'Add Client-Level Manager' : 'Add Region-Level Manager';
  const subtitle = scope === 'client'
    ? (clientName ? `Grant manager access to all teams in ${clientName}.` : '')
    : ((clientName || regionName) ? `Grant manager access to all teams in ${regionName || 'this region'} (${clientName || 'client'}).` : '');

  const handleBack = () => {
    if (scope === 'client') {
      navigate(`/client/${id}`, { state: { clientName } });
      return;
    }
    if (regionClientId) {
      navigate(`/client/${regionClientId}/region/${id}`, {
        state: { clientName, regionName },
      });
      return;
    }
    navigate(-1);
  };

  return (
    <div className="modern-container-narrow">
      <RenderMonitor componentName="ManageAccess" />
      <button type="button" onClick={handleBack} className="modern-button modern-button-secondary modern-mb-6">← Back</button>
      <div className="modern-card">
        <h1 className="modern-title">{heading}</h1>
        {subtitle ? (
          <div className="modern-subtitle" style={{ marginBottom: 12 }}>{subtitle}</div>
        ) : null}
        {loading ? (
          <div className="modern-loading">Loading access list...</div>
        ) : error ? (
          <div className="modern-error">{error}</div>
        ) : (
          <div className="modern-flex modern-flex-wrap modern-gap-3" style={{ marginTop: 8 }}>
            {users.length === 0 ? (
              <span style={{ color: '#94a3b8' }}>No restricted admins for this {scope}.</span>
            ) : (
              users.map(u => {
                const isClientSourceOnRegion = scope === 'region' && u.source === 'client';
                const style = isClientSourceOnRegion ? { backgroundColor: '#eef2ff', border: '1px solid #6366f1' } : undefined;
                return (
                  <div key={String(u.id)} className="modern-user-tag" title={u.email || ''} style={style}>
                    {u.full_name ? u.full_name : u.email}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Add Managers */}
      <div className="modern-card" style={{ marginTop: 16 }}>
        <h2 className="modern-heading">Add Managers</h2>
        <p className="modern-subtitle" style={{ marginBottom: 12 }}>
          Enter one or more emails (comma, space, or newline separated). These users will be able to log in via the Manager Login magic link.
        </p>
        <textarea
          value={addEmails}
          onChange={e => setAddEmails(e.target.value)}
          placeholder="manager1@example.com, manager2@example.com"
          className="modern-textarea"
          style={{ width: '100%', minHeight: 120, resize: 'vertical', marginBottom: 12 }}
          disabled={addLoading}
        />
        <button
          className="modern-button modern-button-success"
          style={{ width: '100%', marginBottom: 12 }}
          disabled={addLoading || !addEmails.trim()}
          onClick={async () => {
            setAddResults([]);
            const emails = (addEmails || '')
              .split(/[\s,]+/)
              .map(e => String(e || '').trim().toLowerCase())
              .filter(e => e && /[^@\s]+@[^@\s]+\.[^@\s]+/.test(e));
            if (emails.length === 0) return;
            setAddLoading(true);
            try {
              const { data: sessionData } = await supabase.auth.getSession();
              const jwt = sessionData?.session?.access_token;
              const payload = scope === 'client'
                ? { emails, client_id: Number(id) }
                : { emails, region_id: Number(id) };
              const resp = await fetch('https://vbjgolyirnbvdfqohpua.supabase.co/functions/v1/admin-grant-managers', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${jwt}`,
                },
                body: JSON.stringify(payload),
              });
              let json = null;
              try { json = await resp.json(); } catch {}
              if (!resp.ok) {
                setAddResults([{ email: '-', status: 'error', message: (json && json.error) ? json.error : 'Request failed' }]);
              } else {
                const list = Array.isArray(json?.results) ? json.results : [];
                setAddResults(list);
                // Refresh list after successful grant
                setReloadKey(k => k + 1);
                setAddEmails('');
              }
            } catch (e) {
              setAddResults([{ email: '-', status: 'error', message: 'Unexpected error' }]);
            }
            setAddLoading(false);
          }}
        >
          {addLoading ? 'Adding…' : 'Add Managers'}
        </button>
        {addResults.length > 0 && (
          <div className="modern-mt-2">
            {addResults.map((r, idx) => (
              <div key={idx} style={{
                color: r.status === 'ok' ? '#166534' : r.status === 'error' ? '#991b1b' : '#1f2937',
                marginBottom: 4,
              }}>
                {r.email}: {r.message}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ManageAccess;


